/**
 * Computed Nodes - Fairy Dust Farm Calculations
 *
 * Compares keeping the inherited fairy dust farm vs selling
 * and investing in the Wizard Index Fund / Dragon Bonds.
 */

import { computed, ComputedNode } from '../../lib/computation-graph';
import { temporalId, compoundGrowth } from '../../lib/computation-graph/temporal';

const YEARS = 11; // 0-10

/**
 * Helper to create year-indexed computed nodes
 */
function forEachYear(
  fn: (year: number) => Record<string, ComputedNode>
): Record<string, ComputedNode> {
  const result: Record<string, ComputedNode> = {};
  for (let year = 0; year < YEARS; year++) {
    Object.assign(result, fn(year));
  }
  return result;
}

// ============================================================================
// BASIC CALCULATIONS
// ============================================================================

export const basicNodes: Record<string, ComputedNode> = {
  // Monthly dust production
  monthlyDustProduction: computed(
    'monthlyDustProduction',
    ['fairyCount', 'dustPerFairy'],
    (fairies, perFairy) => fairies * perFairy,
    {
      label: 'Monthly Dust Production',
      description: 'Total ounces of fairy dust produced per month',
      unit: 'number',
      formula: '{fairyCount} × {dustPerFairy}',
    }
  ),

  // Annual dust production
  annualDustProduction: computed(
    'annualDustProduction',
    ['monthlyDustProduction'],
    (monthly) => monthly * 12,
    {
      label: 'Annual Dust Production',
      description: 'Total ounces of fairy dust produced per year',
      unit: 'number',
      formula: '{monthlyDustProduction} × 12',
    }
  ),

  // Monthly fairy care costs
  monthlyFairyCare: computed(
    'monthlyFairyCare',
    ['fairyCount', 'nectarCostPerFairy'],
    (fairies, nectar) => fairies * nectar,
    {
      label: 'Monthly Fairy Care',
      description: 'Monthly nectar and flower costs for all fairies',
      unit: 'currency',
      formula: '{fairyCount} × {nectarCostPerFairy}',
    }
  ),

  // Elf labor cost (based on minimum wage)
  monthlyElfLabor: computed(
    'monthlyElfLabor',
    ['elfWorkers', 'elvishMinimumWage'],
    (elves, wage) => elves * wage * 30, // 30 days per month
    {
      label: 'Monthly Elf Labor',
      description: 'Monthly wages for elf farm workers',
      unit: 'currency',
      formula: '{elfWorkers} × {elvishMinimumWage} × 30',
    }
  ),

  // Total monthly expenses
  monthlyExpenses: computed(
    'monthlyExpenses',
    ['monthlyFairyCare', 'housingCost', 'sparkleMaintenanceCost', 'monthlyElfLabor', 'magicalLiabilityMinimum'],
    (care, housing, sparkle, labor, insurance) => care + housing + sparkle + labor + insurance / 12,
    {
      label: 'Monthly Expenses',
      description: 'Total monthly operating costs',
      unit: 'currency',
      formula: '{monthlyFairyCare} + {housingCost} + {sparkleMaintenanceCost} + {monthlyElfLabor} + {magicalLiabilityMinimum}/12',
    }
  ),

  // Annual expenses
  annualExpenses: computed(
    'annualExpenses',
    ['monthlyExpenses'],
    (monthly) => monthly * 12,
    {
      label: 'Annual Expenses',
      description: 'Total yearly operating costs',
      unit: 'currency',
      formula: '{monthlyExpenses} × 12',
    }
  ),

  // Net sale proceeds if selling farm now
  initialSaleProceeds: computed(
    'initialSaleProceeds',
    ['farmValue', 'farmSellingCosts'],
    (value, costs) => value * (1 - costs / 100),
    {
      label: 'Net Sale Proceeds',
      description: 'Gold pieces received after gnome realtor fees',
      unit: 'currency',
      formula: '{farmValue} × (1 - {farmSellingCosts}/100)',
    }
  ),

  // Blended investment return (60% Wizard Index, 40% Dragon Bonds)
  blendedReturn: computed(
    'blendedReturn',
    ['wizardIndexReturns', 'dragonBondReturns'],
    (wizard, dragon) => 0.6 * wizard + 0.4 * dragon,
    {
      label: 'Blended Return (60/40)',
      description: '60% Wizard Index / 40% Dragon Bonds',
      unit: 'percent',
      formula: '0.6 × {wizardIndexReturns} + 0.4 × {dragonBondReturns}',
    }
  ),
};

// ============================================================================
// FARM VALUE BY YEAR
// ============================================================================

export const farmValueNodes: Record<string, ComputedNode> = compoundGrowth('farmValue', {
  initialValueId: 'farmValue',
  growthRateId: 'farmAppreciation',
  label: 'Farm Value',
  unit: 'currency',
  years: YEARS,
});

// ============================================================================
// DUST PRICE BY YEAR
// ============================================================================

export const dustPriceNodes: Record<string, ComputedNode> = compoundGrowth('dustPrice', {
  initialValueId: 'dustPrice',
  growthRateId: 'dustPriceGrowth',
  label: 'Dust Price',
  unit: 'currency',
  years: YEARS,
});

// ============================================================================
// ANNUAL REVENUE & PROFIT BY YEAR
// ============================================================================

export const revenueNodes: Record<string, ComputedNode> = forEachYear((year) => {
  const nodes: Record<string, ComputedNode> = {};
  const priceId = temporalId('dustPrice', year);

  // Gross revenue
  const grossRevenueId = temporalId('grossRevenue', year);
  nodes[grossRevenueId] = computed(
    grossRevenueId,
    ['annualDustProduction', priceId],
    (dust, price) => dust * price,
    {
      label: `Gross Revenue (Year ${year})`,
      unit: 'currency',
      formula: `{annualDustProduction} × {${priceId}}`,
    }
  );

  // Revenue after troll tolls
  const afterTollsId = temporalId('revenueAfterTolls', year);
  nodes[afterTollsId] = computed(
    afterTollsId,
    [grossRevenueId, 'trollTollRate'],
    (gross, toll) => gross * (1 - toll / 100),
    {
      label: `Revenue After Tolls (Year ${year})`,
      unit: 'currency',
      formula: `{${grossRevenueId}} × (1 - {trollTollRate}/100)`,
    }
  );

  // Revenue after wizard guild tax
  const afterTaxId = temporalId('revenueAfterTax', year);
  nodes[afterTaxId] = computed(
    afterTaxId,
    [afterTollsId, 'wizardGuildTaxRate'],
    (revenue, tax) => revenue * (1 - tax / 100),
    {
      label: `Revenue After Tax (Year ${year})`,
      unit: 'currency',
      formula: `{${afterTollsId}} × (1 - {wizardGuildTaxRate}/100)`,
    }
  );

  // Net profit (revenue minus expenses - expenses grow with inflation ~2%)
  const netProfitId = temporalId('netProfit', year);
  nodes[netProfitId] = computed(
    netProfitId,
    [afterTaxId, 'annualExpenses'],
    (revenue, expenses) => {
      const inflatedExpenses = expenses * Math.pow(1.02, year);
      return revenue - inflatedExpenses;
    },
    {
      label: `Net Profit (Year ${year})`,
      unit: 'currency',
      formula: `{${afterTaxId}} - {annualExpenses} × 1.02^${year}`,
    }
  );

  return nodes;
});

// ============================================================================
// CUMULATIVE PROFIT (INVESTED AT BLENDED RETURN)
// ============================================================================

export const cumulativeNodes: Record<string, ComputedNode> = forEachYear((year) => {
  const id = temporalId('investedProfits', year);
  const profitId = temporalId('netProfit', year);

  if (year === 0) {
    return {
      [id]: computed(id, [profitId], (profit) => Math.max(0, profit), {
        label: `Invested Profits (Year ${year})`,
        unit: 'currency',
        formula: `max(0, {${profitId}})`,
      }),
    };
  }

  const prevId = temporalId('investedProfits', year - 1);
  return {
    [id]: computed(
      id,
      [prevId, 'blendedReturn', profitId],
      (prev, rate, profit) => prev * (1 + rate / 100) + Math.max(0, profit),
      {
        label: `Invested Profits (Year ${year})`,
        unit: 'currency',
        formula: `{${prevId}} × (1 + {blendedReturn}/100) + max(0, {${profitId}})`,
      }
    ),
  };
});

// ============================================================================
// SELL SCENARIO - INVESTMENT PORTFOLIO
// ============================================================================

export const sellScenarioNodes: Record<string, ComputedNode> = {
  // 100% Wizard Index
  ...compoundGrowth('portfolioWizard', {
    initialValueId: 'initialSaleProceeds',
    growthRateId: 'wizardIndexReturns',
    label: 'Wizard Index Portfolio',
    unit: 'currency',
    years: YEARS,
  }),

  // 60/40 Blended portfolio
  ...compoundGrowth('portfolioBlended', {
    initialValueId: 'initialSaleProceeds',
    growthRateId: 'blendedReturn',
    label: 'Blended Portfolio (60/40)',
    unit: 'currency',
    years: YEARS,
  }),

  // 100% Dragon Bonds
  ...compoundGrowth('portfolioDragon', {
    initialValueId: 'initialSaleProceeds',
    growthRateId: 'dragonBondReturns',
    label: 'Dragon Bond Portfolio',
    unit: 'currency',
    years: YEARS,
  }),
};

// ============================================================================
// EXIT CALCULATIONS (YEAR 10)
// ============================================================================

export const exitNodes: Record<string, ComputedNode> = {
  // Farm scenario total value
  farmTotalValue: computed(
    'farmTotalValue',
    [temporalId('farmValue', 10), temporalId('investedProfits', 10)],
    (farm, profits) => farm + profits,
    {
      label: 'Farm Total Value (Year 10)',
      description: 'Farm value plus reinvested profits',
      unit: 'currency',
      formula: '{farmValue.year10} + {investedProfits.year10}',
    }
  ),

  // Farm scenario selling costs at exit
  farmExitCosts: computed(
    'farmExitCosts',
    [temporalId('farmValue', 10), 'farmSellingCosts'],
    (farm, rate) => farm * rate / 100,
    {
      label: 'Farm Exit Costs',
      description: 'Gnome realtor fees to sell farm at year 10',
      unit: 'currency',
      formula: '{farmValue.year10} × {farmSellingCosts}/100',
    }
  ),

  // Farm scenario net liquidation
  farmNetLiquidation: computed(
    'farmNetLiquidation',
    ['farmTotalValue', 'farmExitCosts'],
    (total, costs) => total - costs,
    {
      label: 'Net Liquidation (Farm)',
      description: 'What you walk away with if you keep the farm',
      unit: 'currency',
      formula: '{farmTotalValue} - {farmExitCosts}',
    }
  ),

  // Sell scenario net liquidation (60/40 portfolio)
  sellNetLiquidation: computed(
    'sellNetLiquidation',
    [temporalId('portfolioBlended', 10)],
    (portfolio) => portfolio,
    {
      label: 'Net Liquidation (Sell)',
      description: 'Portfolio value if you sold and invested',
      unit: 'currency',
      formula: '{portfolioBlended.year10}',
    }
  ),

  // Farm advantage
  farmAdvantage: computed(
    'farmAdvantage',
    ['farmNetLiquidation', 'sellNetLiquidation'],
    (farm, sell) => farm - sell,
    {
      label: 'Farm Advantage',
      description: 'How much better keeping the farm is (can be negative)',
      unit: 'currency',
      formula: '{farmNetLiquidation} - {sellNetLiquidation}',
    }
  ),

  // Fairy dust produced over 10 years
  totalDustProduced: computed(
    'totalDustProduced',
    ['annualDustProduction'],
    (annual) => annual * 10,
    {
      label: 'Total Dust Produced',
      description: 'Lifetime fairy dust production over 10 years',
      unit: 'number',
      formula: '{annualDustProduction} × 10',
    }
  ),
};

// ============================================================================
// COMBINE ALL COMPUTED NODES
// ============================================================================

export const computedNodes: Record<string, ComputedNode> = {
  ...basicNodes,
  ...farmValueNodes,
  ...dustPriceNodes,
  ...revenueNodes,
  ...cumulativeNodes,
  ...sellScenarioNodes,
  ...exitNodes,
};

export default computedNodes;
